import { Button, Flex, Input, Modal, Segmented } from 'antd';
import Password from 'antd/es/input/Password';
import NotificationService from 'core/services/notification';
import { FunctionComponent, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Icon from 'utils/icon';
import styles from './actions.module.scss';
import { useTimer } from 'react-timer-hook';
import dayjs from 'dayjs';
import { onSendFinancialStatementOtp } from './financialStatement/services/financialStatementServices';
import { onSendSuspendOtp } from './suspend/services/suspendServices';
import { VerificationPayload } from './commonModels';
import { onSendAnnouncementOtp } from './announcement/services/anouncementServices';
import { onSendAssemblyActionOtp, onSendAssemblyOtp } from './assemblies/services/assembliesServices';
import { onSendBoardOTP } from './boards/boards/services/boardServices';
import { formatErrorMessages } from 'utils/errorMessage';
import { onSendCapitalRaiseOtp } from './capitals/raise/services/raiseCapitals';
import { onSendDividendOtp } from './dividends/services/dividends';
import { onSendTerminateCommitteeOTP } from './boards/committees/services/committeeServices';
import {
	onSendActivationOtp,
	onSendLoyaltyOtp,
	onSendOtpDistributeLoyaltyProgram,
	onSendOtpCancelDistributionLoyaltyProgram,
} from './loyalityProgram/services/loyaltyProgramService';

interface VerificationModalProps {
	open: boolean;
	isLoading: boolean;
	onVerificationSuccess: (data: VerificationPayload) => void;
	onCancel?: () => void;
	module:
		| 'financialStatement'
		| 'suspend'
		| 'announcement'
		| 'assembly'
		| 'assembly_openVoting'
		| 'assembly_openAttendance'
		| 'board'
		| 'create_capitalRaise_profit_capitalization'
		| 'create_capitalRaise_treasury_shares_acquisition'
		| 'create_capitalRaise_private_equity_issuance'
		| 'create_capitalRaise_transfer_share_fractions'
		| 'update_capitalRaise_profit_capitalization'
		| 'update_capitalRaise_treasury_shares_acquisition'
		| 'update_capitalRaise_private_equity_issuance'
		| 'dividends_create'
		| 'dividends_cancel'
		| 'dividends_update'
		| 'terminate_committee'
		| 'loyalty_program_activate'
		| 'loyalty_program_create'
		| 'loyalty_program_distribute'
		| 'loyalty_program_cancel_distribution';
	id?: string; // to be used for board module
	note?: ReactNode;
	noteType?: 'red' | 'blue';
	noteStyles?: React.CSSProperties;
	otpOnly?: boolean;
	className?: string;
	title?: string;
	subTitle?: string;
}

const VerificationModal: FunctionComponent<VerificationModalProps> = ({
	open,
	isLoading,
	module,
	note,
	id,
	onVerificationSuccess,
	onCancel,
	otpOnly,
	className,
	title,
	subTitle,
	noteType = 'red',
	noteStyles,
}) => {
	const { t } = useTranslation();

	const [isResendLoading, setIsResendLoading] = useState<boolean>(false);
	const [otpSending, setOtpSending] = useState<boolean>(false);
	const [otpSent, setOtpSent] = useState<boolean>(false);

	const [verificationMethod, setVerificationMethod] = useState<'password' | 'otp'>(otpOnly ? 'otp' : 'password');
	const [key, setKey] = useState<string>(''); // This state to store the OTP or password input value
	const [ttl, setTtl] = useState<number>(120);

	const { start, minutes, seconds, totalSeconds, restart } = useTimer({
		expiryTimestamp: dayjs().add(ttl, 'second').toDate(),
		autoStart: false,
	});

	const confirmHandler = async () => {
		// Handle password verification
		if (verificationMethod === 'password') {
			onVerificationSuccess({ method: 'password', key });
			return;
		}

		// Handle otp verification
		if (verificationMethod === 'otp') {
			if (!otpSent) {
				try {
					let response: any;
					setOtpSending(true);

					switch (module) {
						case 'financialStatement':
							response = await onSendFinancialStatementOtp();
							break;
						case 'suspend':
							response = await onSendSuspendOtp();
							break;
						case 'announcement':
							response = await onSendAnnouncementOtp();
							break;
						case 'assembly':
							response = await onSendAssemblyOtp();
							break;
						case 'assembly_openVoting':
							response = await onSendAssemblyActionOtp('open_voting');
							break;
						case 'assembly_openAttendance':
							response = await onSendAssemblyActionOtp('open_attendance');
							break;
						case 'board':
							response = await onSendBoardOTP(id!);
							break;
						case 'terminate_committee':
							response = await onSendTerminateCommitteeOTP(Number(id));
							break;
						case 'create_capitalRaise_transfer_share_fractions':
							response = await onSendCapitalRaiseOtp('CONFIRM_TRANSFER_SHARE_FRACTIONS');
							break;
						case 'create_capitalRaise_profit_capitalization':
							response = await onSendCapitalRaiseOtp('CONFIRM_PROFIT_CAPITALIZATION');
							break;
						case 'update_capitalRaise_profit_capitalization':
							response = await onSendCapitalRaiseOtp('UPDATE_PROFIT_CAPITALIZATION');
							break;
						case 'create_capitalRaise_treasury_shares_acquisition':
							response = await onSendCapitalRaiseOtp('CONFIRM_TREASURY_SHARES_ACQUISITION');
							break;
						case 'update_capitalRaise_treasury_shares_acquisition':
							response = await onSendCapitalRaiseOtp('UPDATE_TREASURY_SHARES_ACQUISITION');
							break;
						case 'create_capitalRaise_private_equity_issuance':
							response = await onSendCapitalRaiseOtp('CONFIRM_PRIVATE_EQUITY_ISSUANCE');
							break;
						case 'update_capitalRaise_private_equity_issuance':
							response = await onSendCapitalRaiseOtp('UPDATE_PRIVATE_EQUITY_ISSUANCE');
							break;
						case 'dividends_create':
							response = await onSendDividendOtp('CONFIRM_DIVIDENDS_DISTRIBUTION');
							break;
						case 'dividends_cancel':
							response = await onSendDividendOtp('CONFIRM_DIVIDENDS_DISTRIBUTION_CANCELLATION');
							break;
						case 'dividends_update':
							response = await onSendDividendOtp('UPDATE_DIVIDENDS_DISTRIBUTION');
							break;
						case 'loyalty_program_activate':
							response = await onSendActivationOtp();
							break;
						case 'loyalty_program_create':
							response = await onSendLoyaltyOtp();
							break;
						case 'loyalty_program_distribute':
							response = await onSendOtpDistributeLoyaltyProgram(Number(id));
							break;
						case 'loyalty_program_cancel_distribution':
							response = await onSendOtpCancelDistributionLoyaltyProgram(Number(id));
							break;
					}

					setTtl(response.ttl);
					setOtpSending(false);

					start();
					NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.OTP_SENT'));
					setOtpSent(true);
				} catch (error: any) {
					setOtpSending(false);

					if (module === 'board') {
						const message = formatErrorMessages(error);
						NotificationService.error(t('COMMON.ERROR'), message);
						return;
					}

					const message = error?.response?.data?.data?.errors?.otp?.[0] || error?.response?.data?.message || t('ACTIONS.OTP_SEND_FAILED');
					NotificationService.error(t('COMMON.ERROR'), message);
				}
			} else {
				onVerificationSuccess?.({ method: 'otp', key });
				return;
			}
		}
	};

	const handleResend = async () => {
		try {
			setIsResendLoading(true);
			switch (module) {
				case 'financialStatement':
					await onSendFinancialStatementOtp();
					break;
				case 'suspend':
					await onSendSuspendOtp();
					break;
				case 'announcement':
					await onSendAnnouncementOtp();
					break;
				case 'assembly':
					await onSendAssemblyOtp();
					break;
				case 'assembly_openVoting':
					await onSendAssemblyActionOtp('open_voting');
					break;
				case 'assembly_openAttendance':
					await onSendAssemblyActionOtp('open_attendance');
					break;
				case 'board':
					await onSendBoardOTP(id!);
					break;
				case 'terminate_committee':
					await onSendTerminateCommitteeOTP(Number(id));
					break;
				case 'create_capitalRaise_profit_capitalization':
					await onSendCapitalRaiseOtp('CONFIRM_PROFIT_CAPITALIZATION');
					break;
				case 'create_capitalRaise_transfer_share_fractions':
					await onSendCapitalRaiseOtp('CONFIRM_TRANSFER_SHARE_FRACTIONS');
					break;
				case 'update_capitalRaise_profit_capitalization':
					await onSendCapitalRaiseOtp('UPDATE_PROFIT_CAPITALIZATION');
					break;
				case 'create_capitalRaise_treasury_shares_acquisition':
					await onSendCapitalRaiseOtp('CONFIRM_TREASURY_SHARES_ACQUISITION');
					break;
				case 'update_capitalRaise_treasury_shares_acquisition':
					await onSendCapitalRaiseOtp('UPDATE_TREASURY_SHARES_ACQUISITION');
					break;
				case 'create_capitalRaise_private_equity_issuance':
					await onSendCapitalRaiseOtp('CONFIRM_PRIVATE_EQUITY_ISSUANCE');
					break;
				case 'update_capitalRaise_private_equity_issuance':
					await onSendCapitalRaiseOtp('UPDATE_PRIVATE_EQUITY_ISSUANCE');
					break;
				case 'dividends_create':
					await onSendDividendOtp('CONFIRM_DIVIDENDS_DISTRIBUTION');
					break;
				case 'dividends_cancel':
					await onSendDividendOtp('CONFIRM_DIVIDENDS_DISTRIBUTION_CANCELLATION');
					break;
				case 'dividends_update':
					await onSendDividendOtp('UPDATE_DIVIDENDS_DISTRIBUTION');
					break;
				case 'loyalty_program_activate':
					await onSendActivationOtp();
					break;
				case 'loyalty_program_create':
					await onSendLoyaltyOtp();
					break;
				case 'loyalty_program_distribute':
					await onSendOtpDistributeLoyaltyProgram(Number(id));
					break;
				case 'loyalty_program_cancel_distribution':
					await onSendOtpCancelDistributionLoyaltyProgram(Number(id));
					break;
			}

			restart(dayjs().add(120, 'second').toDate(), true);

			setIsResendLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.OTP_RESEND_FAILED'));
			setIsResendLoading(false);
		}
	};

	const cancelHandler = () => {
		setOtpSent(false);
		setVerificationMethod(otpOnly ? 'otp' : 'password');
		setKey('');
		onCancel?.();
	};

	return (
		<Modal
			open={open}
			onCancel={cancelHandler}
			onClose={cancelHandler}
			onOk={confirmHandler}
			className={`small confirmationModal ${className} ${note? 'hasNote' : ''}`}
			title={
				<Flex gap={10} align="center">
					<Icon name="info" width={40} height={40} />
					<div>
						<h6 className="mb-0">{title || t('ACTIONS.VERIFICATION')}</h6>
						<span className="text-light" style={{ fontSize: 14 }}>
							{subTitle || t('ACTIONS.CONFIRM_YOUR_REQUEST')}
						</span>
					</div>
				</Flex>
			}
			footer={[
				<Button key="discard" type="link" danger size="middle" onClick={cancelHandler}>
					{t('COMMON.CANCEL')}
				</Button>,
				<Button
					key="confirm"
					variant="solid"
					color="primary"
					size="middle"
					loading={isLoading || otpSending}
					disabled={(verificationMethod === 'password' && key.length < 8) || (verificationMethod === 'otp' && otpSent && key.length < 4)}
					onClick={confirmHandler}>
					{verificationMethod === 'password' || otpSent ? t('COMMON.CONFIRM') : t('ACTIONS.SEND_OTP')}
				</Button>,
			]}>
			<div className={otpOnly ? '' : 'pt-0 pb-3'}>
				{note && (
					<div className={`highlight ${noteType}`} style={noteStyles}>
						<h6 className="fw-medium mb-1">{t('COMMON.NOTE')}</h6>
						{note}
					</div>
				)}

				{!otpOnly && (
					<Segmented
						block
						className="custom white"
						size="middle"
						options={[
							{ value: 'password', label: t('ACTIONS.PASSWORD') },
							{ value: 'otp', label: t('ACTIONS.OTP') },
						]}
						onChange={(value: 'password' | 'otp') => {
							setVerificationMethod(value);
							setKey('');
						}}
						value={verificationMethod}
					/>
				)}

				<div className="mt-4">
					{verificationMethod === 'password' ? (
						<Password placeholder={t('ACTIONS.ENTER_YOUR_PASSWORD')} value={key} onChange={(e) => setKey(e.target.value)} />
					) : (
						otpSent && (
							<>
								<Input.OTP
									length={4}
									type="number"
									size="small"
									onChange={setKey}
									style={{
										transition: 'opacity 0.3s ease',
										opacity: otpSent ? 1 : 0,
									}}
								/>
								<div className={styles.resendDiv}>
									<Button
										color="primary"
										disabled={totalSeconds > 0 || isResendLoading}
										type="link"
										size="small"
										loading={isResendLoading}
										onClick={handleResend}>
										{t('RESET_PASSWORD.RESEND_OTP')}
									</Button>

									<div className={styles.timer}>
										<p>{minutes}</p>
										<p>:</p>
										<p>{seconds}</p>
									</div>
								</div>
							</>
						)
					)}
				</div>
			</div>
		</Modal>
	);
};

export default VerificationModal;
